import {
  INTERVIEW_TYPE,
  PERCENT_TYPE,
  NUMBER_TYPE,
  BOOLEAN_TYPE,
  STRING_TYPE,
  DATE_TYPE,
  BASE_ASSESSMENT_TYPES,
} from "utils/constants"
import {
  formatPercentage,
  toFixedIfNumberHasDecimals,
  isBooleanTrue,
} from "utils/utils"
import { format } from "date-fns"

export const isOfInterviewTypeAtStage = (interviews, interviewType, stage) => {
  const stagesAt =
    BASE_ASSESSMENT_TYPES.find(x => x.id === interviewType)?.stages || []
  return (
    stagesAt.includes(stage?.stage?.id) &&
    interviews?.find(
      interview =>
        interview.stage?.stage?.id === stage.id &&
        interview.type === interviewType
    )
  )
}

export const getBreakdownScoresForStage = (data, stage) => {
  return data.allScores.filter(x => x.stageId === stage?.id)
}

export const getCandidateScore = (data, interviews, stage) => {
  const candidateScoreAtStage = data.candidateScores.find(
    x => x.stage?.id === stage?.id
  )
  const isSOVA =
    isOfInterviewTypeAtStage(interviews, INTERVIEW_TYPE.SOVA_ONLINE, stage) ||
    isOfInterviewTypeAtStage(interviews, INTERVIEW_TYPE.SOVA_ASSESSMENT, stage)

  const isWatsonGlaser = isOfInterviewTypeAtStage(
    interviews,
    INTERVIEW_TYPE.WATSON_GLASER,
    stage
  )

  let candidateScore = candidateScoreAtStage?.score
  if (candidateScore && isSOVA) {
    return toFixedIfNumberHasDecimals(candidateScore)
  }
  if (isWatsonGlaser) {
    return formatPercentage(candidateScore)
  }
  return candidateScore
}

export const getScoresForStage = (configuration, stage) => {
  return configuration?.scores?.find(score => score.stageId === stage?.id)
}

export const isMetricNumber = (scoreDef, metric) => {
  return !scoreDef.metricType ||
    scoreDef.metricType.toUpperCase() === NUMBER_TYPE
    ? !metric?.metricType || metric?.metricType?.toUpperCase() === NUMBER_TYPE
    : metric?.metricType?.toUpperCase() === NUMBER_TYPE
}

export const getTotalScores = (scoreDef, scores) => {
  const totalScoreArray = scores
    ?.filter(x => x.value)
    .filter(x => {
      const scoreMetric = scoreDef?.metrics?.find(
        m => m.label === x.key || m === x.key
      )
      return isMetricNumber(scoreDef, scoreMetric)
    })

  return totalScoreArray?.reduce((a, b) => a + parseFloat(b.value), 0)
}

export const getMetricMin = (def, metric) => {
  if (metric?.min !== undefined) return parseFloat(metric.min, 10)
  if (def?.min !== undefined) return parseFloat(def.min, 10)
  switch (def?.metricType || metric?.metricType) {
    case "number":
    case "percent":
      return 0
    default:
      return undefined
  }
}

export const getMetricMax = (def, metric) => {
  if (metric?.max !== undefined) return parseFloat(metric.max, 10)
  if (def?.max !== undefined) return parseFloat(def.max, 10)
  switch (def?.metricType || metric?.metricType) {
    case "number":
      return 5
    case "percent":
      return 100
    default:
      return undefined
  }
}

export const getMetricType = (def, metric) => {
  if (metric.metricType === "percent") return "number"
  return metric.metricType || def.metricType || "text"
}

const convertUrlsToLinks = text => {
  if (text?.startsWith("https")) {
    const displayText = text.length > 15 ? `${text.substring(0, 15)}…` : text
    return `<a href="${text}" target="_blank" rel="noopener noreferrer">${displayText}</a>`
  }
  return text
}

export const formatScore = (scoreDef, metric, score) => {
  const metricType = (metric.metricType || scoreDef.metricType)?.toUpperCase()
  if (metricType === PERCENT_TYPE) {
    return `${score?.value && `${score.value}%`}`
  } else if (metricType === BOOLEAN_TYPE) {
    return isBooleanTrue(score?.value) ? "Yes" : "No"
  } else if (metricType === STRING_TYPE) {
    return score?.value
  } else if (metricType === DATE_TYPE) {
    return score?.value && format(new Date(score?.value * 1000), "d MMMM yyyy")
  } else {
    const convertToLink = convertUrlsToLinks(score?.value)
    const metricMax = getMetricMax(scoreDef, metric)
    return `${convertToLink || ""} ${metricMax ? `/ ${metricMax}` : ""}`
  }
}

export const getMaxPossibleScore = scoreDef => {
  let totalPossible = 0

  scoreDef?.metrics
    ?.filter(metric => isMetricNumber(scoreDef, metric))
    .forEach(x => {
      totalPossible += getMetricMax(scoreDef, x) || 0
    })
  return totalPossible
}

export const getScoreAtStage = (data, key, stageId) => {
  if (key) {
    return data.allScores.find(
      score => score.key === key && score.stageId === stageId
    )?.value
  }
  return data.candidateScores.find(score => score.stage.id === stageId)?.score
}

export const getCustomizationErrors = (customizations, configurations) => {
  const errors = []
  customizations?.forEach(customization => {
    const value = configurations[customization.label]
    if (customization.required && !value) {
      errors.push(`Please select a ${customization.label}`)
    }
    const validation = customization.validate?.(value)
    if (validation && !validation.valid) {
      errors.push(validation.message)
    }
  })
  return errors
}
